import { useState } from "react";
import axios from "axios";
import { Sparkles, Send, Loader2, ShieldCheck, Lightbulb, Wrench, HardHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLES = [
  "What permits would I need to remodel this house?",
  "How can I resolve this violation?",
  "Can I build an addition on this property?",
  "What does this permit status mean?",
  "What inspections are still required?",
  "What permits are likely required for new windows?",
  "I want to remodel the kitchen and bathrooms. Where do I start?",
  "Can this property be converted into a duplex?",
];

const PROJECTS = [
  "Remodel", "Addition", "New Construction", "Legalize Existing Work", "Violation Resolution",
  "Change of Use", "Duplex / Multifamily", "Windows & Doors", "Roof", "Pool",
  "Electrical", "Plumbing", "Mechanical / A/C", "Other",
];

// Render the AI answer, emphasizing the two required section headers.
const AnswerBlock = ({ text }) => {
  const parts = text.split(/(VERIFIED PROPERTY DATA|PROPERTYCHECK GUIDANCE|DATOS VERIFICADOS DE LA PROPIEDAD|ORIENTACIÓN DE WISE PROPERTY SEARCH)/g);
  return (
    <div className="space-y-1.5 text-sm leading-relaxed text-foreground">
      {parts.map((p, i) => {
        if (p === "VERIFIED PROPERTY DATA" || p === "DATOS VERIFICADOS DE LA PROPIEDAD")
          return <p key={i} className="mt-2 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400"><ShieldCheck className="h-3.5 w-3.5" />{p}</p>;
        if (p === "PROPERTYCHECK GUIDANCE" || p === "ORIENTACIÓN DE WISE PROPERTY SEARCH")
          return <p key={i} className="mt-2 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-accent"><Lightbulb className="h-3.5 w-3.5" />{p}</p>;
        return <p key={i} className="whitespace-pre-wrap">{p.trim()}</p>;
      })}
    </div>
  );
};

export const AskPropertyCheck = ({ report }) => {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", description: "" });

  const send = async (q, projectType) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ask`, {
        question,
        project_type: projectType || project,
        context: report,
        lang,
      });
      setMessages((m) => [...m, { role: "ai", text: data.answer || data.message || "…" }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "The advisor is temporarily unavailable. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const submitAssistance = async () => {
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    try {
      await axios.post(`${API}/permit-assistance`, { ...form, folio: report.folio, address: report.site_address });
      toast.success(t("ask.submitted"));
      setShowForm(false);
      setForm({ name: "", email: "", description: "" });
    } catch {
      toast.error("Could not submit request.");
    }
  };

  const hasViolation = ((report.violations || {}).items || []).length > 0;

  return (
    <div data-testid="ask-propertycheck" className="rounded-xl border border-border bg-card p-6">
      <div className="mb-1 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-accent" />
        <h3 className="font-serif text-xl font-semibold text-foreground">Ask Wise Property Search</h3>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">{t("ask.subtitle")}</p>

      {/* Project selector */}
      <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        <HardHat className="h-3.5 w-3.5" /> {t("ask.project")}
      </p>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {PROJECTS.map((p) => (
          <button
            key={p}
            data-testid="project-type-chip"
            onClick={() => setProject(project === p ? null : p)}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${project === p ? "border-accent bg-accent text-accent-foreground" : "border-border bg-secondary/50 text-foreground/80 hover:border-primary/50"}`}
          >
            {p}
          </button>
        ))}
      </div>

      {hasViolation && (
        <button
          data-testid="violation-advisor-btn"
          onClick={() => send("How can I resolve the code violation on this property? Explain what it means, the likely corrective path, permit and drawing requirements, professionals needed, and the likely inspection/closure sequence.", "Violation Resolution")}
          className="mb-3 flex w-full items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2.5 text-left text-sm font-medium text-amber-700 hover:bg-amber-500/10 dark:text-amber-400"
        >
          <Wrench className="h-4 w-4" /> {t("ask.violationHelp")}
        </button>
      )}

      {/* Conversation */}
      <div className="mb-3 max-h-[420px] space-y-3 overflow-y-auto" data-testid="ask-conversation">
        {messages.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="ml-auto max-w-[85%] rounded-xl rounded-br-sm bg-primary px-3.5 py-2 text-sm text-primary-foreground">{m.text}</div>
          ) : (
            <div key={i} data-testid="ask-answer" className="max-w-[92%] rounded-xl rounded-bl-sm border border-border bg-secondary/40 px-3.5 py-3">
              <AnswerBlock text={m.text} />
            </div>
          )
        )}
        {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> …</div>}
      </div>

      {/* Examples */}
      {messages.length === 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {EXAMPLES.map((q) => (
            <button key={q} data-testid="example-question" onClick={() => send(q)} className="rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-[11px] text-foreground/75 hover:border-primary/50 hover:text-primary">
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
        <Input
          data-testid="ask-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("ask.placeholder")}
          className="h-11 rounded-lg"
        />
        <Button type="submit" data-testid="ask-send-btn" disabled={loading} className="h-11 gap-1.5">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {t("ask.send")}
        </Button>
      </form>

      {/* CTA */}
      <div className="mt-4 rounded-lg border border-border bg-secondary/40 p-4">
        <p className="mb-2 text-sm font-medium text-foreground">{t("ask.cta")}</p>
        {!showForm ? (
          <Button variant="outline" data-testid="request-assistance-btn" onClick={() => setShowForm(true)} className="gap-2">
            {t("ask.ctaBtn")}
          </Button>
        ) : (
          <div className="space-y-2" data-testid="assistance-form">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input placeholder={t("ask.name")} data-testid="assist-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input placeholder={t("ask.email")} data-testid="assist-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <Textarea placeholder={t("ask.desc")} data-testid="assist-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Button data-testid="assist-submit" onClick={submitAssistance}>{t("ask.submit")}</Button>
          </div>
        )}
      </div>

      <p className="mt-4 border-t border-border pt-3 text-[11px] leading-relaxed text-muted-foreground" data-testid="ask-disclaimer">
        {t("ask.disclaimer")}
      </p>
    </div>
  );
};
